import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import apiClient from '../utils/axiosConfig';
import { invalidateBookingsCache } from '../utils/cache';
import { readLastBooking } from './useLastBooking';

const VISITED_KEY = 'adwary_has_visited';

function getDismissKey(businessId) {
  const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  return `adwary_suggestion_dismissed_${businessId}_${today}`;
}

function isDismissed(businessId) {
  try {
    return localStorage.getItem(getDismissKey(businessId)) === '1';
  } catch {
    return true;
  }
}

function markDismissed(businessId) {
  try {
    localStorage.setItem(getDismissKey(businessId), '1');
  } catch { /* ignore */ }
}

export function useQuickRebook({ isAuthenticated, hasBrowsedSalonThisSession }) {
  // Read the flag BEFORE any effects fire (useState initializer runs synchronously).
  // This correctly returns false on the user's very first visit, even though we'll
  // write '1' in an effect later in this same render cycle.
  const [wasReturningVisit] = useState(() => {
    try { return localStorage.getItem(VISITED_KEY) === '1'; } catch { return false; }
  });

  const [visible, setVisible] = useState(false);
  const [lastBooking, setLastBooking] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    // Mark this device as having visited. Runs after the useState initializer has
    // already captured the pre-visit state, so it doesn't affect wasReturningVisit.
    try { localStorage.setItem(VISITED_KEY, '1'); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (hasBrowsedSalonThisSession) return;
    if (!wasReturningVisit) return;

    const lb = readLastBooking();
    if (!lb) return;
    if (isDismissed(lb.businessId)) return;

    setLastBooking(lb);
    setLoading(true);

    Promise.all([
      apiClient.get('/calendar/suggested-slots', {
        params: {
          businessid: lb.businessId,
          reservation_type_id: lb.reservationTypeId,
          limit: 3,
        },
      }),
      apiClient.get('/bookings/my-bookings', {
        params: { businessid: lb.businessId },
      }),
    ])
      .then(([slotsRes, bookingsRes]) => {
        const futureBookings = bookingsRes.data?.bookings ?? [];
        if (futureBookings.length > 0) {
          setVisible(false);
          return;
        }

        const fetchedSlots = slotsRes.data?.slots ?? [];
        if (fetchedSlots.length === 0) {
          setVisible(false);
          return;
        }

        setSlots(fetchedSlots);
        setVisible(true);
      })
      .catch(() => {
        setVisible(false);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isAuthenticated, hasBrowsedSalonThisSession, wasReturningVisit]);

  const dismiss = useCallback(() => {
    if (lastBooking) markDismissed(lastBooking.businessId);
    setVisible(false);
  }, [lastBooking]);

  const selectSlot = useCallback((slot) => {
    setSelectedSlot((prev) =>
      prev && prev.date === slot.date && prev.time === slot.time ? null : slot
    );
  }, []);

  const submitBooking = useCallback(async () => {
    if (!selectedSlot || !lastBooking) return;
    setBooking(true);
    try {
      const response = await apiClient.post('/bookings/create', {
        date: selectedSlot.date,
        time: selectedSlot.time,
        reservation_type_id: lastBooking.reservationTypeId,
        businessid: lastBooking.businessId,
      });
      if (response.data?.success) {
        invalidateBookingsCache();
        toast.success('تم الحجز بنجاح!');
        setVisible(false);
      } else {
        toast.error(response.data?.message || 'حدث خطأ في الحجز');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ في الحجز');
    } finally {
      setBooking(false);
    }
  }, [selectedSlot, lastBooking]);

  return {
    visible,
    lastBooking,
    slots,
    selectedSlot,
    loading,
    booking,
    selectSlot,
    submitBooking,
    dismiss,
  };
}
